import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Save, User, Stethoscope } from 'lucide-react';
import { encounterService } from '../api/encounters';
import { patientService, doctorService } from '../../../lib/services';
import { encounterSchema, type EncounterFormData } from '../schemas/encounterSchema';
import toast from 'react-hot-toast';
import type { Patient, Doctor } from '../../../lib/supabase';

export default function NewConsultation() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [patient, setPatient] = useState<Patient | null>(null);
  const [, setDoctor] = useState<Doctor | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const patientId = searchParams.get('patientId');
  const doctorId = searchParams.get('doctorId');
  const appointmentId = searchParams.get('appointmentId');

  const {
    register: registerEncounter,
    handleSubmit: handleSubmitEncounter,
    formState: { errors },
    setValue,
    getValues,
  } = useForm<EncounterFormData>({
    resolver: zodResolver(encounterSchema),
    defaultValues: {
      patientId: patientId || 'patient-1',
      doctorId: doctorId || 'doctor-1',
      appointmentId: appointmentId || undefined,
      status: 'draft',
    },
  });

  useEffect(() => {
    loadData();
    if (patientId) setValue('patientId', patientId);
    if (doctorId) setValue('doctorId', doctorId);
    if (appointmentId) setValue('appointmentId', appointmentId);
  }, [patientId, doctorId, appointmentId, setValue]);

  const loadData = async () => {
    try {
      const promises = [];
      
      if (patientId) {
        promises.push(patientService.getById(patientId));
      }
      
      if (doctorId) {
        promises.push(doctorService.getById(doctorId));
      }

      const results = await Promise.all(promises);
      
      if (patientId && results[0]) {
        setPatient(results[0] as Patient);
      }
      
      if (doctorId) {
        const doctorIndex = patientId ? 1 : 0;
        if (results[doctorIndex]) {
          setDoctor(results[doctorIndex] as Doctor);
        }
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load patient/doctor information');
    } finally {
      setLoading(false);
    }
  };

  const onSubmitEncounter = async (data: EncounterFormData) => {
    console.log('✅ Form validation passed! Submitting data:', data);
    
    const encounterData = {
      ...data,
      diagnosis: data.primaryDiagnosis ? [{ 
        label: data.primaryDiagnosis, 
        code: data.diagnosisCode || undefined 
      }] : []
    };
    
    setSaving(true);
    try {
      const encounter = await encounterService.create(encounterData);
      console.log('✅ Encounter created successfully:', encounter);
      toast.success('Consultation saved successfully');
      navigate(`/consultations/${encounter.id}`);
    } catch (error: any) {
      console.error('❌ Encounter creation failed:', error);
      toast.error(error.message || 'Failed to save consultation');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <section className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">New Consultation</h1>
          <p className="text-sm text-neutral-600">Create a new patient encounter</p>
        </div>
      </header>

      {/* Patient Header Card */}
      {patient && (
        <div className="bg-white rounded-lg border p-6">
          <div className="flex items-center space-x-4">
            <User className="h-12 w-12 text-blue-600" />
            <div>
              <h3 className="text-lg font-semibold">{patient.name}</h3>
              <p className="text-sm text-neutral-600">{patient.email}</p>
              <p className="text-sm text-neutral-600">{patient.phone}</p>
              {patient.date_of_birth && (
                <p className="text-sm text-neutral-600">
                  DOB: {new Date(patient.date_of_birth).toLocaleDateString()}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      <form 
        onSubmit={(e) => {
          console.log('🔄 Form submit event triggered');
          console.log('🔍 Form errors:', JSON.stringify(errors, null, 2));
          console.log('🔍 Form values:', getValues());
          handleSubmitEncounter(onSubmitEncounter)(e);
        }}
        className="space-y-6"
      >
        {/* Vitals Section */}
        <div className="bg-white rounded-lg border p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <Stethoscope className="w-5 h-5 mr-2" />
            Vitals
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Blood Pressure</label>
              <input
                {...registerEncounter('vitals.bp')}
                placeholder="120/80"
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Heart Rate</label>
              <input
                {...registerEncounter('vitals.hr')}
                type="number"
                placeholder="72"
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Temperature (°F)</label>
              <input
                {...registerEncounter('vitals.temp')}
                type="number"
                step="0.1"
                placeholder="98.6"
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">SpO2 (%)</label>
              <input
                {...registerEncounter('vitals.spo2')}
                type="number"
                placeholder="98"
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">BMI</label>
              <input
                {...registerEncounter('vitals.bmi')}
                type="number"
                step="0.1"
                placeholder="24.5"
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Subjective Section */}
        <div className="bg-white rounded-lg border p-6">
          <h3 className="text-lg font-semibold mb-4">Subjective</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Chief Complaints</label>
              <textarea
                {...registerEncounter('complaints')}
                rows={3}
                placeholder="Patient's main complaints..."
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Clinical Notes</label>
              <textarea
                {...registerEncounter('notes')}
                rows={4}
                placeholder="History of present illness, examination findings..."
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Assessment Section */}
        <div className="bg-white rounded-lg border p-6">
          <h3 className="text-lg font-semibold mb-4">Assessment & Diagnosis</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Primary Diagnosis</label>
              <input
                {...registerEncounter('primaryDiagnosis')}
                placeholder="Enter primary diagnosis..."
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Diagnosis Code (Optional)</label>
              <input
                {...registerEncounter('diagnosisCode')}
                placeholder="ICD-10 or other diagnosis code"
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Status */}
        <div className="bg-white rounded-lg border p-6">
          <h3 className="text-lg font-semibold mb-4">Status</h3>
          <div>
            <label className="block text-sm font-medium text-gray-700">Encounter Status</label>
            <select
              {...registerEncounter('status')}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="draft">Draft</option>
              <option value="final">Final</option>
            </select>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate('/consultations')}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            <span>{saving ? 'Saving...' : 'Save Consultation'}</span>
          </button>
        </div>
      </form>
    </section>
  );
}
